import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, ShoppingBag } from 'lucide-react';
import api from '../api/client';
import Modal from '../components/Modal';
import { ErrMsg } from '../components/FormField';
import { PageHeader, SearchInput, FilterSelect, FilterBar } from '../components/FilterBar';
import DateRangePicker from '../components/DateRangePicker';

const ACCESSORY_ITEMS = [
  'Mobile Tempered Glass','Mobile Back Cover','Laptop Power Adapter','Laptop Power Cable',
  'Mobile Phone Charger Cable','Mobile Phone Power Adapter','Wired Mouse','Wireless Mouse','USB Drive'
];
const STATUS_COLORS = {
  submitted: '#f59e0b', pending_board_approval: '#818cf8', approved: '#34d399',
  rejected_by_it: '#ef4444', rejected_by_board: '#ef4444', completed: '#10b981',
};
const STATUS_LABELS = {
  submitted: 'Submitted — Awaiting IT', pending_board_approval: 'Pending Board Approval',
  approved: 'Approved', rejected_by_it: 'Rejected by IT',
  rejected_by_board: 'Rejected by Board', completed: 'Completed'
};
const STATUS_OPTIONS = [
  { value: 'submitted', label: 'Submitted' },
  { value: 'pending_board_approval', label: 'Pending Board Approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected_by_it', label: 'Rejected by IT' },
  { value: 'rejected_by_board', label: 'Rejected by Board' },
  { value: 'completed', label: 'Completed' },
];

export default function MyAccessoryRequests() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ items: [], other: '', showOther: false, reason: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 20, page, status: filterStatus || undefined, from: dateFrom || undefined, to: dateTo || undefined };
      const r = await api.get('/accessory-requests', { params });
      let data = r.data.data || [];
      // Client-side search filter (item names + reason + request number)
      if (search.trim()) {
        const s = search.toLowerCase();
        data = data.filter(row =>
          row.request_number?.toLowerCase().includes(s) ||
          (row.requested_items || []).some(i => i.toLowerCase().includes(s)) ||
          row.other_item?.toLowerCase().includes(s) ||
          row.reason?.toLowerCase().includes(s)
        );
      }
      setRows(data);
      setTotal(r.data.total || 0);
      setPages(r.data.pages || 1);
    } catch {} finally { setLoading(false); }
  }, [page, search, filterStatus, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async e => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      if (!form.items.length && !form.other?.trim()) { setError('Select at least one item or describe in Other.'); setSaving(false); return; }
      if (!form.reason?.trim()) { setError('Reason is required.'); setSaving(false); return; }
      await api.post('/accessory-requests', {
        requested_items: form.items,
        other_item: form.other?.trim() || undefined,
        reason: form.reason
      });
      setModal(null); setForm({ items: [], other: '', showOther: false, reason: '' }); load();
    } catch (err) { setError(err.response?.data?.error || 'Submission failed'); } finally { setSaving(false); }
  };

  const clearFilters = () => { setSearch(''); setFilterStatus(''); setDateFrom(''); setDateTo(''); setPage(1); };

  return (
    <div className="space-y-4">
      <PageHeader title="My IT Accessory Requests" count={total}>
        <button onClick={load} className="btn-ghost"><RefreshCw size={13} className={loading ? 'animate-spin' : ''}/></button>
        <button onClick={() => { setForm({ items: [], other: '', showOther: false, reason: '' }); setError(''); setModal('req'); }}
          className="btn-primary"><Plus size={13}/> New Request</button>
      </PageHeader>

      <FilterBar hasFilters={!!(search || filterStatus || dateFrom || dateTo)} onClear={clearFilters}>
        <SearchInput value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="Search request #, item, reason..."/>
        <FilterSelect value={filterStatus} onChange={v => { setFilterStatus(v); setPage(1); }} placeholder="All Statuses" options={STATUS_OPTIONS}/>
        <DateRangePicker from={dateFrom} to={dateTo} onChange={(f, t) => { setDateFrom(f); setDateTo(t); setPage(1); }}/>
      </FilterBar>

      {loading ? (
        <div className="flex items-center justify-center h-32 gap-3" style={{color:'#334155'}}>
          <div className="w-4 h-4 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"/>Loading...
        </div>
      ) : rows.length === 0 ? (
        <div className="glass glow-border rounded-2xl p-12 text-center">
          <ShoppingBag size={40} style={{color:'#1e3a5f',margin:'0 auto 12px'}}/>
          <div className="text-sm" style={{color:'#334155'}}>
            {filterStatus || search || dateFrom || dateTo ? 'No requests match your filters' : 'No accessory requests yet'}
          </div>
          {!filterStatus && !search && !dateFrom && !dateTo && (
            <div className="text-xs mt-1" style={{color:'#1e3a5f'}}>Use the "New Request" button to request IT accessories</div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r, i) => {
            const color = STATUS_COLORS[r.status] || '#475569';
            const items = [...(r.requested_items || []), r.other_item].filter(Boolean).join(', ');
            return (
              <div key={i} className="glass glow-border rounded-2xl p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="font-mono text-xs font-bold" style={{color:'#60a5fa'}}>{r.request_number || '—'}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{background:`${color}18`,color,border:`1px solid ${color}30`}}>
                        {STATUS_LABELS[r.status] || r.status}
                      </span>
                    </div>
                    <div className="text-sm font-medium" style={{color:'#e2e8f0'}}>{items || '—'}</div>
                    <div className="text-xs mt-1" style={{color:'#475569'}}>{r.reason}</div>
                    {r.it_notes && <div className="text-xs mt-2 italic" style={{color:'#60a5fa'}}>IT Notes: {r.it_notes}</div>}
                    {r.rejection_reason && <div className="text-xs mt-1 italic" style={{color:'#f87171'}}>Rejection: {r.rejection_reason}</div>}
                    {r.approved_by && <div className="text-xs mt-1" style={{color:'#34d399'}}>Approved by: {r.approved_by}</div>}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-xs" style={{color:'#334155'}}>{r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB') : ''}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex justify-center gap-2">
          {Array.from({length: pages}, (_, i) => i + 1).map(p => (
            <button key={p} onClick={() => setPage(p)}
              className="text-xs px-3 py-1.5 rounded-lg transition-all"
              style={{background: p === page ? 'rgba(59,130,246,0.2)' : 'rgba(255,255,255,0.04)', color: p === page ? '#60a5fa' : '#475569', border: `1px solid ${p === page ? 'rgba(59,130,246,0.3)' : 'rgba(255,255,255,0.06)'}`}}>
              {p}
            </button>
          ))}
        </div>
      )}

      {modal === 'req' && (
        <Modal title="Request IT Accessories" onClose={() => { setModal(null); }} size="lg">
          <form onSubmit={handleSubmit} className="space-y-4">
            <ErrMsg msg={error}/>
            <div>
              <label className="block text-xs font-semibold mb-3" style={{color:'#94a3b8'}}>1. Select Required Accessories *</label>
              <div className="space-y-2">
                {ACCESSORY_ITEMS.map(item => (
                  <label key={item} className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer"
                    style={{ background: form.items.includes(item) ? 'rgba(59,130,246,0.12)' : 'rgba(255,255,255,0.03)',
                             border: `1px solid ${form.items.includes(item) ? 'rgba(59,130,246,0.35)' : 'rgba(255,255,255,0.06)'}` }}>
                    <input type="checkbox" checked={form.items.includes(item)}
                      onChange={e => setForm(prev => ({
                        ...prev, items: e.target.checked ? [...prev.items, item] : prev.items.filter(i => i !== item)
                      }))} className="accent-blue-500 w-4 h-4"/>
                    <span className="text-sm" style={{color:'#e2e8f0'}}>{item}</span>
                  </label>
                ))}
                <label className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer"
                  style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
                  <input type="checkbox" checked={!!form.showOther}
                    onChange={e => setForm(prev => ({...prev, showOther: e.target.checked, other: e.target.checked ? prev.other : ''}))}
                    className="accent-blue-500 w-4 h-4"/>
                  <span className="text-sm" style={{color:'#e2e8f0'}}>Other</span>
                  {form.showOther && (
                    <input type="text" value={form.other} onChange={e => setForm(prev => ({...prev, other: e.target.value}))}
                      placeholder="Describe the item" className="input-dark flex-1 text-xs py-1" style={{marginLeft:8}}/>
                  )}
                </label>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-2" style={{color:'#94a3b8'}}>2. Reason for Request *</label>
              <textarea value={form.reason} onChange={e => setForm(prev => ({...prev, reason: e.target.value}))}
                rows={3} required className="input-dark" placeholder="Explain why you need this / any other remarks..."/>
            </div>
            <div className="px-4 py-3 rounded-xl text-xs" style={{background:'rgba(245,158,11,0.08)',border:'1px solid rgba(245,158,11,0.2)',color:'#fbbf24'}}>
              Please note there is a waiting time of 1-2 weeks and all requests are subject to prior approval.
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setModal(null)} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">{saving ? 'Submitting...' : 'Submit Request'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
